// src/auth/authApi.ts
import { apiClient } from "@maat/api-base";
var takePrimedAuthState = async () => {
  if (typeof window === "undefined" || !window.__maatAuthState) return void 0;
  const pending = window.__maatAuthState;
  delete window.__maatAuthState;
  try {
    const response = await pending;
    if (!response.ok) return void 0;
    return await response.json();
  } catch {
    return void 0;
  }
};
var authApi = {
  // ── Adaptive Login ───────────────────────────────────────────
  loginRequest: async (data) => {
    return await apiClient.post(
      "/main/api/auth/login/request",
      data
    );
  },
  loginWithPassword: async (data) => {
    return await apiClient.post("/main/api/auth/login/password", data);
  },
  loginWithOtp: async (data) => {
    return await apiClient.post("/main/api/auth/login/otp", data);
  },
  loginWithOAuth: async (data) => {
    const { provider, ...body } = data;
    return await apiClient.post(`/main/api/auth/oauth/${provider}`, body);
  },
  loginWithMfa: async (data) => {
    return await apiClient.post("/main/api/auth/login/mfa", data);
  },
  // ── Registration ─────────────────────────────────────────────
  requestOtp: async (data) => {
    return await apiClient.post("/main/api/auth/register", data);
  },
  registerVerify: async (data) => {
    return await apiClient.post(
      "/main/api/auth/register/verify",
      data
    );
  },
  registerComplete: async (data) => {
    return await apiClient.post("/main/api/auth/register/complete", data);
  },
  resendOtp: async (data) => {
    return await apiClient.post("/main/api/otp/send", data);
  },
  // ── Phone sign-in ────────────────────────────────────────────
  /** Which sign-in surfaces this environment offers (D10). Public, no-store. */
  capabilities: async () => {
    return await apiClient.get("/main/api/auth/capabilities");
  },
  /** The proof-of-work challenge the CLIENT solves before a send is accepted. */
  phoneChallenge: async () => {
    return await apiClient.post(
      "/main/api/auth/phone/challenge",
      {}
    );
  },
  phoneSend: async (data) => {
    return await apiClient.post("/main/api/auth/phone/send", data);
  },
  phoneVerify: async (data) => {
    return await apiClient.post(
      "/main/api/auth/phone/verify",
      data
    );
  },
  // ── Forgot password (phone) ──────────────────────────────────
  forgotPasswordSend: async (data) => {
    return await apiClient.post(
      "/main/api/auth/password/forgot/send",
      data
    );
  },
  forgotPasswordVerify: async (data) => {
    return await apiClient.post(
      "/main/api/auth/password/forgot/verify",
      data
    );
  },
  resetPassword: async (data) => {
    return await apiClient.post("/main/api/auth/password/reset", data);
  },
  // ── Passkey sign-in ──────────────────────────────────────────
  /** Opens a ceremony, or answers `{ fallback: 'PHONE_OTP' }` — which is also what an unknown number gets. */
  passkeyAuthOptions: async (data) => {
    return await apiClient.post(
      "/main/api/auth/passkeys/auth-options",
      data
    );
  },
  passkeyAuthComplete: async (data) => {
    return await apiClient.post(
      "/main/api/auth/passkeys/auth-complete",
      data
    );
  },
  /** For the ceremony that succeeded but relayed no proof — poll with the same handle. */
  passkeyReconcile: async (data) => {
    return await apiClient.post(
      "/main/api/auth/passkeys/reconcile",
      data
    );
  },
  // ── Admin ────────────────────────────────────────────────────
  /** Google-OAuth admin sign-in (mints the admin_token + admin_refresh cookies). */
  adminLoginWithOAuth: async (data) => {
    const { provider, ...body } = data;
    return await apiClient.post(
      `/main/api/auth/admin/oauth/${provider}`,
      body
    );
  },
  /** Silently rotate the admin session (called on 401 / app focus). */
  adminRefresh: async () => {
    return await apiClient.post("/main/api/auth/admin/refresh");
  },
  /** The admin auth signal — resolves the current admin or 401s. */
  adminMe: async () => {
    return await apiClient.get("/main/api/auth/admin/me");
  },
  adminLogout: async () => {
    return await apiClient.post("/main/api/auth/admin/logout");
  },
  // ── Session ──────────────────────────────────────────────────
  /** Silently rotate the consumer session (also fired automatically by the 401 interceptor). */
  refresh: async () => {
    return await apiClient.post("/main/api/auth/refresh");
  },
  logout: async () => {
    return await apiClient.post("/main/api/auth/logout");
  },
  state: async () => {
    const primed = await takePrimedAuthState();
    if (primed) return primed;
    return await apiClient.get("/main/api/auth/state");
  },
  me: async () => {
    return await apiClient.get(
      // eslint-disable-next-line maat/api-call-scope -- session bootstrap: returns the authenticated user + permission claims
      "/main/api/users/me"
    );
  },
  // ── Permissions ──────────────────────────────────────────────
  getPermissions: async () => {
    return await apiClient.get("/main/api/auth/permissions");
  },
  checkPermissions: async (slugs) => {
    return await apiClient.post(
      "/main/api/auth/permissions/check",
      { permissions: slugs }
    );
  },
  // ── Per-Member Permission Management ─────────────────────────────────────
  /** Read the direct permissions + role IDs for a specific workspace member. */
  getUserPermissions: async (userId) => {
    return await apiClient.get(
      `/main/api/auth/permissions/${userId}`
    );
  },
  /** Full-replace direct permissions (and optionally assign a role) for a member. */
  assignPermissions: async (userId, data) => {
    return await apiClient.put(`/main/api/auth/permissions/${userId}`, data);
  }
};

export {
  authApi
};
